/// <reference lib="dom" />

import { AUTHORIZATION_REFUSAL_MESSAGE, isRawAuthorizationReason } from './authorization-refusal';
import { parseJsonWithNativeSafeNumbers, stringifyLosslessJson } from './lossless-json-codec';
import { z } from 'zod';
import type { SafeParseResult } from './zod-utils';
import { NestJsExceptionResponseSchema } from './schemas/common.schemas';

export interface HttpRequestHeaders {
    readonly [headerName: string]: string;
}

/*
 * This uses the global Fetch `RequestInit` for cross-runtime compatibility. In browsers it comes from `lib.dom`;
 * in Node it comes from `@types/node` (backed by undici) as a type-only dependency. We narrow only `headers`
 * to a string map to keep the API stable and serializable across runtimes.
 */
export interface FetchRequestInit extends Omit<RequestInit, 'headers'> {
    readonly headers?: HttpRequestHeaders;
}

export type FetchImplementation = (
    input: string | URL,
    init?: FetchRequestInit
) => Promise<Response>;

type FetchArguments = [input: string | URL, init?: FetchRequestInit];

export interface PillarApiClientOptions {
    readonly onSessionExpired?: () => void;
    readonly baseUrl?: string;
    /** Maximum requests in flight through response-body consumption; omitted means unlimited. */
    readonly maxConcurrentRequests?: number;
    readonly fetchImplementation?: FetchImplementation;
    readonly getRequestHeaders?: () => HttpRequestHeaders;
    readonly enableIdempotencyHeaders?: boolean;
    readonly onResponse?: (response: Response) => void;
    readonly onTraceContext?: (traceHeaderValue: string) => void;
    readonly onValidationError?: (error: ApiValidationError) => void;
}

export interface PillarApiRequestOptions {
    readonly signal?: AbortSignal;
    readonly method?: string;
    readonly body?: unknown;
    readonly headers?: HttpRequestHeaders;
    readonly handle401AsSessionExpired?: boolean;
    readonly acceptedStatusCodes?: ReadonlyArray<number>;
    readonly idempotencyKey?: string | null;
    readonly keepalive?: boolean;
    readonly preserveLosslessNumbers?: boolean;
}

export class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly statusText: string,
        message: string,
        public readonly body?: unknown,
        /** The raw response text, for logs and diagnostics only; never shown (BUG-261). */
        public readonly rawBody?: string
    ) {
        super(message);
        this.name = 'ApiError';
    }

    /** Only the validated server message, never the transport's raw-body fallback. */
    public get responseMessage(): string | undefined {
        const parseResult = NestJsExceptionResponseSchema.safeParse(this.body);
        if (!parseResult.success) {
            return undefined;
        }
        const message = parseResult.data.message;
        return Array.isArray(message) ? message.join(', ') : message;
    }
}

const GATEWAY_FAILURE_STATUSES: ReadonlyArray<number> = [502, 503, 504];

/**
 * Whether a request failed because the backend could not be reached: fetch
 * rejected with its network-failure TypeError (the client rethrows it
 * unchanged), or a proxy or load balancer answered 502/503/504 with a body
 * that is not the API's own JSON. A 503 the backend itself sends carries its
 * JSON error body and is not counted.
 */
export function isPillarApiUnreachableError(error: unknown): boolean {
    if (error instanceof TypeError) {
        return true;
    }
    return (
        error instanceof ApiError &&
        GATEWAY_FAILURE_STATUSES.includes(error.status) &&
        error.body === undefined
    );
}

export interface ApiResponseValidationError {
    readonly kind: 'response-body';
    readonly endpoint: string;
    readonly issues: z.core.$ZodIssue[];
    readonly response: unknown;
}

export interface ApiTraceHeaderValidationError {
    readonly kind: 'trace-header';
    readonly headerValue: string;
    readonly issues: z.core.$ZodIssue[];
}

export type ApiValidationError = ApiResponseValidationError | ApiTraceHeaderValidationError;

export interface PillarApiRequestExecutor {
    readonly baseUrl: string;
    requestWithSchema<T>(
        endpoint: string,
        schema: z.ZodSchema<T>,
        options?: PillarApiRequestOptions
    ): Promise<T>;
}

function isFormDataBody(value: unknown): value is FormData {
    return value instanceof FormData;
}

function isMutatingHttpMethod(method: string): boolean {
    return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase());
}

function generateIdempotencyKey(): string {
    const cryptoObject = globalThis.crypto;
    const randomUuidParseResult = z
        .function({
            input: [],
            output: z.string(),
        })
        .safeParse(cryptoObject.randomUUID.bind(cryptoObject));
    if (randomUuidParseResult.success) {
        return randomUuidParseResult.data();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isJsonContentType(contentType: string | null): boolean {
    if (contentType === null) {
        return false;
    }
    const normalized = contentType.toLowerCase().trim();
    return normalized.startsWith('application/json') || normalized.startsWith('application/json;');
}

const CHECK_BEFORE_RESUBMITTING = 'Check the operation status before submitting again.';

/**
 * Friendly text for a failed response that carries no server message. It
 * never contains the HTTP status or response body, which stay available on
 * the error for logs (BUG-261).
 */
export function getHttpFailureMessage(status: number | undefined): string {
    if (status === undefined)
        return `Unexpected response from the server. ${CHECK_BEFORE_RESUBMITTING}`;
    if (status === 401) return 'Your session has ended. Sign in again, then retry.';
    if (status === 403) return AUTHORIZATION_REFUSAL_MESSAGE;
    if (status === 404) return 'The requested item was not found. Refresh the page and try again.';
    if (status === 408 || status === 504)
        return `The server took too long to respond. ${CHECK_BEFORE_RESUBMITTING}`;
    if (status === 409)
        return 'This conflicts with a change made elsewhere. Refresh the page and try again.';
    if (status === 413) return 'This request is too large. Reduce its size and try again.';
    if (status === 429) return 'Too many requests right now. Wait a minute, then try again.';
    if (status === 502) return `Temporary upstream network error. ${CHECK_BEFORE_RESUBMITTING}`;
    if (status === 503) return `Service temporarily unavailable. ${CHECK_BEFORE_RESUBMITTING}`;
    if (status >= 400 && status < 500)
        return 'The server could not accept this request. Check the details and try again.';
    return `The server had a problem handling this request. ${CHECK_BEFORE_RESUBMITTING}`;
}

// Traceability: implements REQ-LEGACY-APPROVED.
export function extractErrorMessage(
    responseBody: string,
    response: Response,
    status: number
): string {
    // A raw status or response body is never a user message (BUG-261); only a
    // server message in the API's own error shape is shown.
    if (!responseBody || responseBody.trim() === '') {
        return getHttpFailureMessage(status);
    }

    const contentType: string | null = response.headers.get('content-type');
    if (!isJsonContentType(contentType)) {
        return getHttpFailureMessage(status);
    }

    let parsed: unknown;
    try {
        parsed = parseJsonWithNativeSafeNumbers(responseBody);
    } catch {
        return getHttpFailureMessage(status);
    }

    const result = NestJsExceptionResponseSchema.safeParse(parsed);
    if (result.success) {
        const validationErrors = result.data.errors;
        if (validationErrors !== undefined && validationErrors.length > 0) {
            const validationErrorSummaries: ReadonlyArray<string> = validationErrors.map(
                (validationError): string => {
                    const validationPath: string =
                        validationError.path !== undefined
                            ? validationError.path
                            : '(unknown path)';
                    const validationMessage: string =
                        validationError.message !== undefined
                            ? validationError.message
                            : 'validation error';
                    return `${validationPath}: ${validationMessage}`;
                }
            );
            const baseMessageParseResult = z.string().safeParse(result.data.message);
            const baseMessage: string = baseMessageParseResult.success
                ? baseMessageParseResult.data
                : 'Validation failed';
            return `${baseMessage} (${validationErrorSummaries.join('; ')})`;
        }
        // A raw authorization reason stays on ApiError.body for diagnostics (BUG-267).
        const messageParseResult = z.string().safeParse(result.data.message);
        if (messageParseResult.success) {
            return isRawAuthorizationReason(messageParseResult.data)
                ? AUTHORIZATION_REFUSAL_MESSAGE
                : messageParseResult.data;
        }
        const messageListParseResult = z.array(z.string()).safeParse(result.data.message);
        if (messageListParseResult.success) {
            const message: string = messageListParseResult.data.join(', ');
            return isRawAuthorizationReason(message) ? AUTHORIZATION_REFUSAL_MESSAGE : message;
        }
    }

    return getHttpFailureMessage(status);
}

function parseErrorBody(responseBody: string, response: Response): unknown {
    if (!responseBody || responseBody.trim() === '') {
        return undefined;
    }
    const contentType: string | null = response.headers.get('content-type');
    if (!isJsonContentType(contentType)) {
        return undefined;
    }
    try {
        return parseJsonWithNativeSafeNumbers(responseBody);
    } catch {
        return undefined;
    }
}

export function dedupe<ArgumentsTuple extends readonly unknown[], ReturnValue>(
    functionToDedupe: (...args: ArgumentsTuple) => Promise<ReturnValue>
): (...args: ArgumentsTuple) => Promise<ReturnValue> {
    const cacheBySerializedArguments = new Map<string, Promise<ReturnValue>>();

    return async (...args: ArgumentsTuple): Promise<ReturnValue> => {
        const serializedArgumentsKey = stringifyLosslessJson(args);
        const existingPromise = cacheBySerializedArguments.get(serializedArgumentsKey);
        if (existingPromise !== undefined) {
            return existingPromise;
        }

        const dedupedPromise = functionToDedupe(...args);
        cacheBySerializedArguments.set(serializedArgumentsKey, dedupedPromise);
        return await dedupedPromise.finally(() => {
            cacheBySerializedArguments.delete(serializedArgumentsKey);
        });
    };
}

export async function cancelUnreadPillarApiResponse(response: Response | null): Promise<void> {
    if (response !== null && response.body !== null && !response.bodyUsed) {
        try {
            await response.body.cancel();
        } catch {
            // Cleanup must not replace the original request or callback failure.
        }
    }
}

export function createPillarApiRequestScheduler(maxConcurrentRequestsOption?: number) {
    const maxConcurrentRequests = maxConcurrentRequestsOption ?? Infinity;
    if (
        maxConcurrentRequestsOption !== undefined &&
        (!Number.isSafeInteger(maxConcurrentRequests) || maxConcurrentRequests < 1)
    ) {
        throw new RangeError('maxConcurrentRequests must be a positive safe integer.');
    }
    let activeRequests = 0;
    const pendingRequests: Array<() => void> = [];

    async function acquireRequestSlot(abortSignal?: AbortSignal): Promise<void> {
        abortSignal?.throwIfAborted();
        if (activeRequests < maxConcurrentRequests) {
            activeRequests += 1;
            return;
        }
        await new Promise<void>((resolve, reject) => {
            const start = (): void => {
                abortSignal?.removeEventListener('abort', cancel);
                activeRequests += 1;
                resolve();
            };
            const cancel = (): void => {
                const index = pendingRequests.indexOf(start);
                if (index !== -1) pendingRequests.splice(index, 1);
                reject(abortSignal?.reason);
            };
            pendingRequests.push(start);
            abortSignal?.addEventListener('abort', cancel, { once: true });
        });
    }

    return async function schedulePillarApiRequest<T>(
        request: () => Promise<T>,
        abortSignal?: AbortSignal
    ): Promise<T> {
        await acquireRequestSlot(abortSignal);
        try {
            abortSignal?.throwIfAborted();
            return await request();
        } finally {
            activeRequests -= 1;
            pendingRequests.shift()?.();
        }
    };
}

export function createPillarApiRequestExecutor(
    options: PillarApiClientOptions
): PillarApiRequestExecutor {
    const baseUrl: string = options.baseUrl ?? '/api';
    const onSessionExpired: (() => void) | undefined = options.onSessionExpired;
    const fetchImplementation: FetchImplementation =
        options.fetchImplementation ??
        ((...args: FetchArguments) => {
            return globalThis.fetch(...args);
        });
    const getRequestHeaders: (() => HttpRequestHeaders) | undefined = options.getRequestHeaders;
    const enableIdempotencyHeaders: boolean = options.enableIdempotencyHeaders ?? false;
    const onResponse: ((response: Response) => void) | undefined = options.onResponse;
    const onTraceContext: ((traceHeaderValue: string) => void) | undefined = options.onTraceContext;
    const onValidationError: ((error: ApiValidationError) => void) | undefined =
        options.onValidationError;
    const traceHeaderSchema: z.ZodType<string> = z.string().min(1);
    const httpPermanentRedirects: Record<string, URL> = {};
    const schedulePillarApiRequest = createPillarApiRequestScheduler(options.maxConcurrentRequests);

    function resolveBaseUrlForRequest(): string {
        let baseUrlForResolution: string = baseUrl;
        if (baseUrlForResolution.startsWith('/')) {
            // eslint-disable-next-line no-restricted-syntax
            const locationValue: unknown = (globalThis as unknown as Record<string, unknown>)
                .location;
            const locationParseResult = z
                .object({ origin: z.string().min(1) })
                .passthrough()
                .safeParse(locationValue);
            if (!locationParseResult.success) {
                throw new Error(
                    `Invalid baseUrl "${baseUrlForResolution}": relative baseUrl requires globalThis.location.origin`
                );
            }
            baseUrlForResolution = `${locationParseResult.data.origin}${baseUrlForResolution}`;
        } else if (
            !baseUrlForResolution.startsWith('https://') &&
            !baseUrlForResolution.startsWith('http://')
        ) {
            throw new Error(
                `Invalid baseUrl "${baseUrlForResolution}": must start with "https://", "http://", or "/"`
            );
        }

        return baseUrlForResolution;
    }

    function buildRequestUrl(endpoint: string): URL {
        const baseUrlForResolution: string = resolveBaseUrlForRequest();
        const fullPath: string = endpoint.startsWith('/')
            ? `${baseUrlForResolution}${endpoint}`
            : `${baseUrlForResolution}/${endpoint}`;
        return new URL(fullPath);
    }

    async function executePillarApiRequest<T>(
        endpoint: string,
        schema: z.ZodSchema<T>,
        options: PillarApiRequestOptions = {}
    ): Promise<T> {
        const defaultHeaders: HttpRequestHeaders = getRequestHeaders ? getRequestHeaders() : {};
        const {
            method = 'GET',
            body,
            headers = {},
            handle401AsSessionExpired = true,
            acceptedStatusCodes = [],
            idempotencyKey,
        } = options;
        const normalizedMethod = method.toUpperCase();
        const locationHeaderSchema: z.ZodType<string> = z.string().min(1);
        const isMultipartFormDataBody = isFormDataBody(body);
        const requestHeaders: Record<string, string> = {
            Accept: 'application/json',
            ...defaultHeaders,
            ...headers,
        };
        if (
            enableIdempotencyHeaders &&
            isMutatingHttpMethod(normalizedMethod) &&
            idempotencyKey !== null
        ) {
            requestHeaders['Idempotency-Key'] = idempotencyKey ?? generateIdempotencyKey();
        }
        if (!isMultipartFormDataBody) {
            requestHeaders['Content-Type'] = 'application/json';
        }

        const requestInit: FetchRequestInit = {
            signal: options.signal,
            method: normalizedMethod,
            headers: requestHeaders,
            body:
                body === undefined
                    ? undefined
                    : isMultipartFormDataBody
                      ? body
                      : stringifyLosslessJson(body),
            credentials: 'include',
        };

        const requestUrlAsURL: URL = buildRequestUrl(endpoint);
        const requestUrl: string = requestUrlAsURL.toString();
        let currentUrlAsURL: URL = httpPermanentRedirects[requestUrl] ?? requestUrlAsURL;
        let response: Response | null = null;
        const maxRedirects = 3;
        let redirectIsTransient: boolean = false;
        try {
            for (let redirectCount = 0; redirectCount <= maxRedirects; redirectCount += 1) {
                response = await fetchImplementation(currentUrlAsURL, requestInit);
                if (onResponse) {
                    onResponse(response);
                }
                if (response.status >= 300 && response.status < 400) {
                    await response.text();
                    const locationHeaderValue: string | null = response.headers.get('location');
                    if (locationHeaderValue === null) {
                        throw new ApiError(
                            response.status,
                            response.statusText,
                            'Redirect response missing Location header'
                        );
                    }
                    const locationParseResult = locationHeaderSchema.safeParse(locationHeaderValue);
                    if (!locationParseResult.success) {
                        throw new ApiError(
                            response.status,
                            response.statusText,
                            'Redirect response has invalid Location header'
                        );
                    }
                    currentUrlAsURL = new URL(locationParseResult.data, currentUrlAsURL);
                    redirectIsTransient =
                        redirectIsTransient || ![301, 308].includes(response.status);
                    if (!redirectIsTransient) {
                        httpPermanentRedirects[requestUrl] = currentUrlAsURL;
                    }
                    continue;
                }
                if (response.status >= 400) {
                    delete httpPermanentRedirects[requestUrl];
                }
                break;
            }
            if (response === null) {
                throw new ApiError(500, 'Invalid Response', 'No response received from server');
            }

            const traceHeaderValue: string | null = response.headers.get('x-cloud-trace-context');
            if (traceHeaderValue !== null) {
                const traceHeaderParseResult: SafeParseResult<string> =
                    traceHeaderSchema.safeParse(traceHeaderValue);
                if (!traceHeaderParseResult.success) {
                    const validationError: ApiTraceHeaderValidationError = {
                        kind: 'trace-header',
                        headerValue: traceHeaderValue,
                        issues: traceHeaderParseResult.error.issues,
                    };
                    if (onValidationError) {
                        onValidationError(validationError);
                    }
                    throw new ApiError(
                        500,
                        'Invalid Response',
                        'Invalid X-Cloud-Trace-Context header'
                    );
                }
                if (onTraceContext) {
                    onTraceContext(traceHeaderParseResult.data);
                }
            }

            const responseBody = await response.text();

            if (response.status === 401) {
                if (onSessionExpired && handle401AsSessionExpired) {
                    onSessionExpired();
                }
                const errorBody: unknown = parseErrorBody(responseBody, response);
                const errorMessage = extractErrorMessage(responseBody, response, response.status);
                throw new ApiError(
                    response.status,
                    response.statusText,
                    errorMessage,
                    errorBody,
                    responseBody
                );
            }

            if (!response.ok && !acceptedStatusCodes.includes(response.status)) {
                const errorBody: unknown = parseErrorBody(responseBody, response);
                const errorMessage = extractErrorMessage(responseBody, response, response.status);
                throw new ApiError(
                    response.status,
                    response.statusText,
                    errorMessage,
                    errorBody,
                    responseBody
                );
            }

            if (response.status === 204) {
                return schema.parse(undefined);
            }

            if (!responseBody || responseBody.trim() === '') {
                throw new ApiError(response.status, response.statusText, 'Empty response body');
            }

            const contentType: string | null = response.headers.get('content-type');
            if (!isJsonContentType(contentType)) {
                throw new ApiError(
                    response.status,
                    response.statusText,
                    `Expected JSON response but got Content-Type: ${contentType ?? 'missing'}`
                );
            }

            let parsed: unknown;
            try {
                parsed = parseJsonWithNativeSafeNumbers(responseBody);
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                throw new ApiError(
                    response.status,
                    response.statusText,
                    `Failed to parse JSON response: ${errorMessage}`
                );
            }

            try {
                return schema.parse(parsed);
            } catch (validationError: unknown) {
                if (validationError instanceof z.ZodError) {
                    const validationErrorDetails: ApiResponseValidationError = {
                        kind: 'response-body',
                        endpoint,
                        issues: validationError.issues,
                        response: parsed,
                    };
                    if (onValidationError) {
                        onValidationError(validationErrorDetails);
                    }
                    throw new ApiError(
                        500,
                        'Invalid Response',
                        `API response validation failed: ${validationError.issues
                            .map((issue: z.core.$ZodIssue) => issue.message)
                            .join(', ')}`
                    );
                }
                throw validationError;
            }
        } finally {
            await cancelUnreadPillarApiResponse(response);
        }
    }

    async function fetchPillarApi<T>(
        endpoint: string,
        schema: z.ZodSchema<T>,
        options: PillarApiRequestOptions = {}
    ): Promise<T> {
        return schedulePillarApiRequest(
            () => executePillarApiRequest(endpoint, schema, options),
            options.signal
        );
    }

    return {
        baseUrl,
        requestWithSchema: fetchPillarApi,
    };
}
